import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import Grid from '@/global/components/ui/Grid'
import { Button } from 'global/components/Button'
import { FormDatePicker, FormField } from 'global/components/Form'
import { SelectFuncionarios } from 'global/components/Selects'
import ContentGuide from 'global/guides/ContentGuide/ContentGuide'
import { queryClient } from 'providers/ReactQuery'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { centroCusto } from 'service/api'
import { toast } from 'sonner'
import { DesassociarFuncionarioCentroCusto } from '../modals/DesassociarFuncionario'
import {
    ErroAssociacaoFuncionario,
    ErrosAssociacaoModal,
    FuncionarioAssociado,
} from '../modals/ErrosAssociacao'

type AssociarFuncionarioCentroCustoInput = {
    funcionarioIds: number[]
    dtInicial: string
    dtFinal?: string
}

type AssociarFuncionarioCentroCustoOutput = {
    associados: FuncionarioAssociado[]
    erros: ErroAssociacaoFuncionario[]
}

export const AssociarFuncionarioForm = () => {
    const { watch } = useFormContext<{
        id: number
        dtInicial: string
        dtFinal?: string
    }>()
    const { id, dtInicial, dtFinal } = watch()
    const { data, isLoading } = useQuery(['centroCusto', id], () =>
        centroCusto.getById(id),
    )

    const { mutate, isLoading: isAssociando } = useMutation(
        (input: AssociarFuncionarioCentroCustoInput) =>
            centroCusto.patchFrom<
                AssociarFuncionarioCentroCustoInput,
                AssociarFuncionarioCentroCustoOutput
            >(id, 'funcionarios', input),
    )

    const [checkedIds, setCheckedIds] = useState<number[]>([])
    const [associados, setAssociados] = useState<FuncionarioAssociado[]>([])
    const [erros, setErros] = useState<ErroAssociacaoFuncionario[]>([])
    const checked = data?.FuncionarioCentroCusto?.map(
        item => item.funcionarioId,
    )

    const handleChange = (ids: number[]) => {
        setCheckedIds(ids)
    }

    const handleClick = () => {
        mutate(
            {
                funcionarioIds: checkedIds,
                dtInicial,
                dtFinal: dtFinal || undefined,
            },
            {
                onSuccess: ({
                    associados: associadosResposta,
                    erros: errosResposta,
                }) => {
                    if (associadosResposta.length) {
                        toast.success(
                            `${associadosResposta.length} funcionário(s) associado(s) com sucesso`,
                        )
                        queryClient.invalidateQueries(['centroCusto', id])
                    }

                    if (errosResposta.length) {
                        setAssociados(associadosResposta)
                        setErros(errosResposta)
                    } else {
                        setCheckedIds([])
                    }
                },
                onError: (error: any) => {
                    toast.error(
                        error?.message ?? 'Erro ao associar funcionário(s)',
                    )
                },
            },
        )
    }

    return (
        <Grid container columnSpacing={2} className="items-start">
            <Grid item xs={9}>
                <SelectFuncionarios
                    form
                    checked={checked}
                    onChange={handleChange}
                    width={'100%'}
                />
            </Grid>
            <Grid item xs={3}>
                <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleClick}
                    disabled={!checkedIds.length || !dtInicial || isAssociando}
                >
                    Associar
                </Button>
            </Grid>
            <Grid item xs={6}>
                <FormDatePicker
                    label="Data inicial"
                    name="dtInicial"
                    type="date"
                    required
                />
            </Grid>
            <Grid item xs={6}>
                <FormDatePicker
                    label="Data final"
                    name="dtFinal"
                    type="date"
                    helperText={'Deixe em branco para indeterminado'}
                />
            </Grid>
            <Grid item xs={12} className="h-[350px]">
                <ContentGuide.DataGrid
                    loading={isLoading}
                    columns={[
                        {
                            field: 'Funcionario',
                            headerName: 'Nome',
                            flex: 1,
                            valueGetter: ({ value }) => value.nome,
                        },
                        {
                            ...optionsColumn,
                            field: '__desassociar',
                            renderCell: DesassociarFuncionarioCentroCusto,
                        },
                    ]}
                    rows={data?.FuncionarioCentroCusto ?? []}
                    ToolbarProps={{ hideToolbar: true }}
                    hideFooter
                />
            </Grid>
            <FormField name="id" hidden />
            <ErrosAssociacaoModal
                associados={associados}
                erros={erros}
                onClose={() => setErros([])}
            />
        </Grid>
    )
}
